import React, { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import classNames from "classnames";
import { useAtom, useAtomValue } from "jotai";
import { IntentSentence } from "@/model";
import { useDeleteIntentSentence, useUpdateIntentSentence, useUpdateIntentSentenceAnswer } from "@/query/sentence";
import { intentSearchKeywordAtom, intentSearchTypeAtom, intentTableEditAtom } from "@/atom/intentTable";
import { useDialog, useModal } from "@/hook";
import HighlightText from "@/component/HighlightText";
import { errorToast, successToast } from "@/component/Toast";
import { DeleteIcon, DocumentIcon, EditIcon } from "@/assets/svgs/icons";

import { SENTENCE_ANSWER_MODAL_ID } from "../SentenceAnswerEditModal";


export interface SentenceFragmentProps {
    sentence: IntentSentence;
    activeIntent: boolean;
    isLast: boolean;
}

const SentenceFragment: React.FC<SentenceFragmentProps> = (props) => {
    const { sentence, activeIntent, isLast } = props;

    const intl = useIntl();
    const modal = useModal();
    const dialog = useDialog();

    const [isEditing, setIsEditing] = useState<boolean>(false);

    const [intentTableEdit, setIntentTableEdit] = useAtom(intentTableEditAtom);
    const searchKeyword = useAtomValue(intentSearchKeywordAtom);
    const searchType = useAtomValue(intentSearchTypeAtom);

    const { mutateAsync: updateSentenceAnswer } = useUpdateIntentSentenceAnswer(
        sentence.agent_id,
        sentence.skill_id,
        sentence.intent_id,
        sentence.key
    );
    const { mutateAsync: deleteSentence } = useDeleteIntentSentence(
        sentence.agent_id,
        sentence.skill_id,
        sentence.intent_id,
        sentence.key
    );

    const onClickAnswer = () => {
        const submit = async (answers: string[]) => {
            await updateSentenceAnswer({ answers })
                .then(() => {
                    successToast(intl.formatMessage({ id: "t003" }));
                    modal.close(SENTENCE_ANSWER_MODAL_ID);
                })
                .catch(() => {
                    errorToast(intl.formatMessage({ id: "t004" }));
                });
        };
        modal.open(
            SENTENCE_ANSWER_MODAL_ID,
            {
                answers:sentence.phrase_answers,
                onSubmit: submit
            }
        );
    };

    const onClickDelete = () => {
        dialog.open({
            variant: "danger",
            title: intl.formatMessage({ id: "i03052" }),
            content: intl.formatMessage({ id: "i03053" }),
            confirmText: intl.formatMessage({ id: "b0008" }),
            onConfirm: async () => {
                deleteSentence()
                    .then(() => {
                        successToast(intl.formatMessage({ id: "t005" }));
                    })
                    .catch(() => {
                        errorToast(intl.formatMessage({ id: "t006" }));
                    });
            }
        });
    };

    useEffect(() => {
        if (intentTableEdit[`${sentence.key}_sentence`] !== undefined) {
            setIsEditing(true);
        }
        // eslint-disable-next-line
    }, []);

    if (isEditing)
        return (
            <SentenceEditForm sentence={sentence}
                              endEditing={() => {
                                  setIsEditing(false);
                                  setIntentTableEdit(prev => {
                                      delete prev[`${sentence.key}_sentence`];
                                      return prev;
                                  });
                              }}
            />
        );

    return (
        <div role="row"
             className={classNames(
                 "group h-table-row pl-[58px] pr-5 flex justify-between items-center bg-white border-b border-transparent",
                 isLast && "border-b-transparent",
                 !activeIntent && "opacity-50"
             )}
        >
            <div role="cell"
                 className="selection-table-td flex gap-1 text-black-400 text-[14px] font-[400] leading-[1.5]"
            >
                •
                {searchType === "intentSentence" ? (
                    <HighlightText query={sentence.origin_sentence}
                                   keyword={searchKeyword}
                                   highlightClassName="text-highlight"
                    />
                ) : (
                    <span>{sentence.origin_sentence}</span>
                )}
            </div>
            <div role="cell"
                 className="opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 flex gap-3"
            >
                <button onClick={() => setIsEditing(true)}>
                    <EditIcon className="size-3.5 text-black-400" />
                </button>
                <button onClick={onClickAnswer}>
                    <DocumentIcon className="size-3.5 text-black-400" />
                </button>
                <button onClick={onClickDelete}>
                    <DeleteIcon className="size-3.5 text-point-red" />
                </button>
            </div>
        </div>
    );
};

interface SentenceEditFormProps {
    sentence: IntentSentence;
    endEditing: () => void;
}

const SentenceEditForm: React.FC<SentenceEditFormProps> = (props) => {
    const { sentence, endEditing } = props;
    const intl = useIntl();
    const inputRef = useRef<HTMLInputElement>(null);
    const [validated, setValidated] = useState<boolean>(false);

    const [intentTableEdit, setIntentTableEdit] = useAtom(intentTableEditAtom);
    const { mutateAsync: updateSentence, isPending: isUpdating } = useUpdateIntentSentence(
        sentence.agent_id,
        sentence.skill_id,
        sentence.intent_id,
        sentence.key
    );

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const newSentence = inputRef.current && inputRef.current.value.trim();
        if (!newSentence) {
            setValidated(true);
            return;
        }

        await updateSentence({ sentence: newSentence })
            .then(() => {
                successToast(intl.formatMessage({ id: "t003" }));
                endEditing();
            })
            .catch(() => {
                errorToast(intl.formatMessage({ id: "t004" }));
            });
    };

    const onInput = () => {
        setIntentTableEdit(prev => {
            prev[`${sentence.key}_sentence`] = inputRef.current?.value ?? "";
            return prev;
        });
    };

    const onBlur = () => {
        endEditing();
    };

    useEffect(() => {
        setTimeout(() => {
            inputRef.current?.focus();
            if (intentTableEdit[`${sentence.key}_sentence`] !== undefined)
                inputRef.current!.value = intentTableEdit[`${sentence.key}_sentence`];
        }, 0);
        // eslint-disable-next-line
    }, []);

    return (
        <div role="row">
            <form className="relative h-table-row pl-[58px] selection-table-form"
                  onSubmit={onSubmit}
            >
                <input type="text"
                       ref={inputRef}
                       className={classNames(validated && "input-invalid")}
                       placeholder={intl.formatMessage({ id: "i03072" })}
                       required={validated}
                       defaultValue={sentence.origin_sentence}
                       disabled={isUpdating}
                       onInput={onInput}
                       onBlur={onBlur}
                />
            </form>
        </div>
    );
};

export default SentenceFragment;
